import {
	CalendarCommonModule,
	CalendarDayModule,
	CalendarEvent,
	CalendarEventTimesChangedEvent,
	CalendarMonthModule,
	CalendarWeekModule,
} from 'angular-calendar';
import { Subject } from 'rxjs';

import { CommonModule } from '@angular/common';
import {
	AfterViewInit,
	ChangeDetectionStrategy,
	Component,
	EventEmitter,
	inject,
	Input,
	OnChanges,
	OnInit,
	Output,
	SimpleChanges,
} from '@angular/core';
import { CalendarWeekHeadersComponent } from '@schedule/lib/components/calendar-week-headers/calendar-week-headers.component';
import { CalendarEventOffcanvasService } from '@schedule/lib/services/calendar-event-offcanvas-service/calendar-event-offcanvas-service.service';
import { ScheduledEvent } from '@schedule/models/calendar/calendar.model';
import { CalendarSettings, CalendarSettingsService } from '@schedule/services/calendar/calendar-settings/calendar-settings.service';

@Component({
	selector: 'app-calendar-week-view',
	imports: [CalendarWeekModule, CalendarCommonModule, CommonModule, CalendarWeekHeadersComponent, CalendarMonthModule, CalendarDayModule],
	changeDetection: ChangeDetectionStrategy.OnPush,
	templateUrl: './calendar-week-view.component.html',
	styleUrl: './calendar-week-view.component.scss',
})
export class CalendarWeekViewComponent implements OnInit, OnChanges, AfterViewInit {
	protected calendarSettings: CalendarSettings;

	@Input() events: CalendarEvent[];
	@Input() refresh: Subject<void>;
	@Input() viewDate: Date;
	@Output() eventClicked = new EventEmitter<CalendarEvent>();
	@Output() eventTimesChanged = new EventEmitter<CalendarEventTimesChangedEvent>();

	private calendarSettingsService = inject(CalendarSettingsService);
	private calendarOffcanvasService = inject(CalendarEventOffcanvasService);

	ngOnInit() {
		this.calendarSettings = this.calendarSettingsService.getDefaultSettings();
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes.events.currentValue && this.calendarOffcanvasService.currentEvent) {
			const event = this.events.find(
				(e: CalendarEvent<ScheduledEvent>) =>
					e.meta?.scheduledAssessmentId === this.calendarOffcanvasService.currentEvent?.meta?.scheduledAssessmentId,
			);
			if (event) {
				this.calendarOffcanvasService.updateCalendarEvent(event);
			}
		}

		if (changes.events.currentValue) {
			this.resizeCalendarEvents();
		}
	}

	ngAfterViewInit(): void {
		this.resizeCalendarEvents();

		setTimeout(() => {
			const timeElements = document.querySelectorAll('.cal-time');
			const timeTarget = Array.from(timeElements).find((el) => el.textContent?.trim() === '6 AM');

			if (timeTarget) {
				timeTarget.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'center' });
			}
		}, 300);
		setTimeout(() => {
			const todayElement = document.querySelectorAll('.cal-today');

			if (todayElement.length > 0) {
				todayElement[0].scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'center' });
			}
		}, 600);
	}

	resizeCalendarEvents(): void {
		setTimeout(() => {
			const eventsContainerElements = document.querySelectorAll('.cal-events-container');

			eventsContainerElements.forEach((container) => {
				const events = Array.from(container.querySelectorAll<HTMLElement>('.cal-event-container'));

				events.forEach((event, _, allEvents) => {
					const eventRect = event.getBoundingClientRect();

					// Get all overlapping events (including self)
					const overlappingEvents = allEvents.filter((other) => {
						if (other === event) return true;
						const otherRect = other.getBoundingClientRect();

						const topA = eventRect.top;
						const bottomA = eventRect.bottom;
						const topB = otherRect.top;
						const bottomB = otherRect.bottom;

						// Check vertical overlap
						return bottomA > topB && topA < bottomB;
					});

					// Sort overlapping events by their left % so we can assign z-index
					overlappingEvents.sort((a, b) => {
						const leftA = parseFloat(getComputedStyle(a).left);
						const leftB = parseFloat(getComputedStyle(b).left);
						return leftA - leftB;
					});

					// Apply widths and z-index
					overlappingEvents.forEach((el, i) => {
						const left = el.style.left;

						if (i === overlappingEvents.length - 1) {
							el.style.width = `calc(100% - ${left})`;
						} else {
							el.style.width = `calc(85% * ${2 / overlappingEvents.length})`;
						}

						el.style.zIndex = `${i + 5}`;
					});
				});
			});
		});
	}

	handleEventClicked(event: CalendarEvent): void {
		this.eventClicked.emit(event);
	}

	handleEventTimesChanged(event: CalendarEventTimesChangedEvent): void {
		this.eventTimesChanged.emit(event);
	}

	showCalendarOffcanvas(event: CalendarEvent<ScheduledEvent>): void {
		this.calendarOffcanvasService.showOffcanvas(event);
	}
}
